// Wire-format Zod schemas for auth-svc HTTP endpoints.
//
// These are the shapes the frontend (and any service-to-service caller)
// imports to share the contract. Routes inside auth-svc validate against
// the Request schemas; their handlers return typed payloads matching the
// Response schemas.
//
// The Response shapes intentionally exclude the password hash and never
// echo refresh tokens once stored; raw refresh tokens are only present in
// the immediate response to /login or /refresh.
import { z } from "zod";
import { brandSummarySchema } from "./brands.js";
// Canonical role enum across the wire (ADR-0020 Phase 9). Mirrors the
// auth_memberships.role CHECK constraint added in Phase 1. Single
// source of truth — auth-svc emits, core-api verifies, FE consumes,
// all import from here.
export const ROLE_KEYS = [
    "admin",
    "inspector",
    "company_manager",
    "brand_manager",
    "store_manager",
    "facility_supervisor",
    // Vocabulario propio de postventa (ADR-0090, 2026-09-12). Los nombres
    // salen de la minuta del 2026-08-18 —"Gerente de Postventa, Jefe de
    // Proyecto, Propietario"— y no del trío que este ADR había propuesto,
    // porque la minuta manda sobre el ADR.
    //
    // No es limpieza de nomenclatura: es una frontera de seguridad. Hasta
    // ahora postventa tomaba prestadas las claves de facility, y
    // `store_manager` —etiquetado "Cliente" en esa receta— pertenece a los
    // CUATRO conjuntos de capacidad de más abajo. Un propietario invitado
    // con esa clave entraba al dashboard gerencial y alcanzaba el chat de
    // IA con agregados de TODA la inmobiliaria. En facility esa clave
    // designa personal de la empresa; en postventa designaría a un tercero
    // externo. Misma clave, nivel de confianza opuesto.
    "gerente_postventa",
    "jefe_proyecto",
    "propietario",
];
export const roleSchema = z.enum(ROLE_KEYS);
/**
 * Roles que designan a alguien ajeno a la organización dueña del tenant.
 *
 * `propietario` es el primero: no es empleado de la inmobiliaria, es el
 * dueño de una vivienda con una relación de postventa de años. Vive acá,
 * y no como una lista implícita "todo lo que no está en los conjuntos de
 * capacidad", para que agregar un rol externo nuevo obligue a decidir
 * explícitamente qué NO puede hacer.
 *
 * El test que acompaña este archivo verifica el complemento: ningún rol
 * externo puede aparecer en ninguno de los conjuntos de capacidad. El
 * modo de falla que ese test existe para atrapar es alguien agregando
 * `propietario` a FINDING_MANAGEMENT_ROLE_KEYS "para que vea su propia
 * observación" y abriendo de paso el hilo de delegación entero.
 */
export const EXTERNAL_PARTY_ROLE_KEYS = ["propietario"];
/** True cuando el rol designa a un tercero externo al tenant. */
export function isExternalPartyRole(role) {
    return EXTERNAL_PARTY_ROLE_KEYS.includes(role);
}
// Facility role/dashboard access matrix (ADR-0043, amended ADR-0089).
// `DASHBOARD_ROLE_KEYS` used to answer four unrelated questions at nine
// call sites — reach the dashboard shell, reach the finding lifecycle,
// reach the org-aggregate analytics surfaces — with one constant. Split
// three ways by capability so each gate names what it actually defends
// and a future role change touches one set, not a shared one that
// silently widens siblings. All three import from here; no consumer
// re-derives its own Set or re-types the literal.
// May load the gerencial dashboard shell at all: the dashboard's
// RequireDashboardRole guard, the PWA's "Ir al dashboard" button,
// auth-svc's handoff mint + exchange (ADR-0045), and /facility-dashboard.
// admin + company_manager ("Gerente") see the whole org; store_manager
// ("Jefe de tienda") sees only their store(s); brand_manager their brand;
// facility_supervisor ("Supervisor") sees their assigned store(s) or zona
// (ADR-0089 — previously PWA-only, ADR-0043's original exclusion).
// residential `inspector` never reaches a facility surface.
export const DASHBOARD_SHELL_ROLE_KEYS = [
    "admin",
    "company_manager",
    "store_manager",
    "brand_manager",
    "facility_supervisor",
    // Postventa: el equipo de la inmobiliaria "opera todo desde el
    // dashboard" (minuta 04). `propietario` queda fuera a propósito — su
    // superficie es la PWA y sólo su unidad.
    "gerente_postventa",
    "jefe_proyecto",
];
/** True when a role is allowed to load the gerencial dashboard shell. */
export function isDashboardShellRole(role) {
    return DASHBOARD_SHELL_ROLE_KEYS.includes(role);
}
// May read one finding's detail thread and move its delegation lifecycle
// (GET /findings/:id, POST /findings/:id/delegation-status, ADR-0074).
// Always composed with `loadScopedFinding` — this predicate 403s before
// any row lookup (depends only on auth.role, not an enumeration oracle),
// loadScopedFinding then 404s any row outside the caller's asset scope,
// indistinguishably from a nonexistent one. Neither check replaces the
// other (ADR-0086 Decision 2 shape).
export const FINDING_MANAGEMENT_ROLE_KEYS = [
    "admin",
    "company_manager",
    "store_manager",
    "brand_manager",
    "facility_supervisor",
    "gerente_postventa",
    "jefe_proyecto",
];
/** True when a role may reach a finding's detail + delegation lifecycle. */
export function isFindingManagementRole(role) {
    return FINDING_MANAGEMENT_ROLE_KEYS.includes(role);
}
// May use the org-aggregate management-analytics surfaces: the postventa
// cartera tablero (cartera-dashboard.ts, ADR-0072) and the postventa
// insights (postventa-dashboard.ts, ADR-0132). The gerencial IA chat moved
// to IA_CHAT_ROLE_KEYS under ADR-0139 — do not derive one set from the
// other.
export const MANAGEMENT_ANALYTICS_ROLE_KEYS = [
    "admin",
    "company_manager",
    "store_manager",
    "brand_manager",
    // Postventa: el tablero de cartera (Proyectos y Unidades) y los
    // insights de IA son la superficie donde el jefe de proyecto hace su
    // "rol secundario" — contratistas que tardan, especialidades con más
    // fallas, tiempo de resolución. `jefe_proyecto` entra aunque su scope
    // de datos sea asset_group: el conjunto decide si la superficie existe
    // para él, `loadScopedAssets` decide qué filas ve dentro.
    "gerente_postventa",
    "jefe_proyecto",
];
/** True when a role may use the org-aggregate analytics surfaces. */
export function isManagementAnalyticsRole(role) {
    return MANAGEMENT_ANALYTICS_ROLE_KEYS.includes(role);
}
// May use the gerencial IA chat (ia-chat.ts, ADR-0050) — a ROLE-SCOPED
// surface since ADR-0139: every fact in the block comes from
// loadScopedAssets, so an asset_group-scoped role sees its own stores
// and nothing else. Enumerated on purpose, NOT composed from
// MANAGEMENT_ANALYTICS_ROLE_KEYS (ADR-0089 option A: composition is
// silent widening; cartera and postventa insights stay org-aggregate and
// stay closed to facility_supervisor).
//   - facility_supervisor: founder decision 2026-08-17, reaffirmed
//     2026-10-12 (ADR-0139).
//   - gerente_postventa / jefe_proyecto: kept so the set equals today's
//     chat audience; the recipe gate in ia-chat.ts is what stops them,
//     not this set (iaChatEnabled capability is the follow-up, #0468).
//   - brand_manager: in the set. Its scope is now projected by the JWT
//     claim `brand` (ADR-0164), but brand-scoped roles are READ-ONLY in
//     v1 and have no lane in the delegation thread (ADR-0134); that is
//     why canEditDelegationStatus is NOT widened for them.
export const IA_CHAT_ROLE_KEYS = [
    "admin",
    "company_manager",
    "store_manager",
    "brand_manager",
    "facility_supervisor",
    "gerente_postventa",
    "jefe_proyecto",
];
/** True when a role may use the gerencial IA chat (role-scoped, ADR-0139). */
export function isIaChatRole(role) {
    return IA_CHAT_ROLE_KEYS.includes(role);
}
// Roles that may write delegation status org-wide, unscoped — no asset
// predicate needed because these two roles already see every asset in
// the org. Deliberately NOT the `auth.role !== "admin"` check that
// /delegate uses: that one predates the ADR-0043 access matrix and would
// 403 the company_manager ("Gerente") this surface exists to serve.
export const DELEGATION_STATUS_EDIT_ROLE_KEYS = [
    "admin",
    "company_manager",
    // El equivalente postventa de company_manager: alcance organización,
    // ve todos los proyectos.
    "gerente_postventa",
];
/** True when a role may write delegation status org-wide, unscoped. */
export function isOrgScopedDelegationEditor(role) {
    return DELEGATION_STATUS_EDIT_ROLE_KEYS.includes(role);
}
// Roles that may write delegation status on their OWN scoped asset(s)
// only — loadScopedFinding narrows the row set, this only decides
// whether the affordance is offered at all. Kept separate from
// DELEGATION_STATUS_EDIT_ROLE_KEYS, which is reserved for org-wide,
// unscoped writers; these two roles are asset-scoped by design (ADR-0089).
export const ASSET_SCOPED_DELEGATION_EDITOR_ROLE_KEYS = [
    "store_manager",
    "facility_supervisor",
    // Scope asset_group: asigna dentro de sus proyectos, no org-wide.
    "jefe_proyecto",
];
/**
 * True when a role can actually complete a delegation-status write.
 *
 * Narrower than `isFindingManagementRole` on purpose. `brand_manager`
 * reaches the finding-management surface, and its scope is already
 * projected by the JWT claim `brand` (ADR-0164), but brand-scoped roles
 * are READ-ONLY in v1 (founder 2026-10-19, see READ_ONLY_ROLE_KEYS) and
 * have no lane in the delegation thread (ADR-0134). Rendering an editable
 * control for that role would be offering an affordance core-api rejects
 * with 403 ROLE_READ_ONLY — that is why canEditDelegationStatus is NOT
 * widened for it.
 */
export function canEditDelegationStatus(role) {
    return (isOrgScopedDelegationEditor(role) ||
        ASSET_SCOPED_DELEGATION_EDITOR_ROLE_KEYS.includes(role));
}
// May mutate a report they did not author: PUT /reports/:id,
// DELETE /reports/:id, POST /reports/:id/area-captures (ADR-0093). A
// second authorization axis, composed with — never replacing — the
// org/asset scope loadScopedReport already applies. Author-only is the
// rule; this set is the named, explicit exception.
//
// One member today, and that is the point. This is NOT the antipattern
// documented at DELEGATION_STATUS_EDIT_ROLE_KEYS above — that note is
// about a bare `auth.role !== "admin"` literal scattered at a call
// site, which predated the ADR-0043 matrix and 403'd company_manager on
// a surface built to serve them. A named capability set with one member
// is the opposite: it says what it defends, it is greppable, and
// widening it is one edit here with the ADR reasoning attached, not
// nine call sites.
//
// company_manager is deliberately out. There, company_manager is IN
// (DELEGATION_STATUS_EDIT_ROLE_KEYS) because delegating a finding to a
// provider is an org-wide management action the Gerente's role is
// defined by. Here the capability is overwriting another member's
// authorship of an in-progress acta — authorUserId is an attestation of
// who was physically on site, feeds ~20 attribution sites and the
// signed PDF. Letting a Gerente silently edit or discard an inspector's
// field evidence is an integrity property, not a management
// convenience — confirmed as the deliberate product decision, not
// inherited by omission.
export const REPORT_AUTHORSHIP_OVERRIDE_ROLE_KEYS = ["admin"];
/** True when a role may mutate a report it did not author (ADR-0093). */
export function isReportAuthorshipOverrideRole(role) {
    return REPORT_AUTHORSHIP_OVERRIDE_ROLE_KEYS.includes(role);
}
// May create invitations via POST /auth/invitations (ADR-0112), subject to
// the escalation cap in INVITATION_GRANTABLE_ROLE_KEYS_BY_INVITER below.
// `admin` always could; `company_manager` ("Gerente") and
// `facility_supervisor` ("Supervisor") are newly admitted so facility orgs
// can self-service onboard without routing every hire through a platform
// admin. `inspector`, `brand_manager`, `store_manager` are excluded from
// being inviters — not requested this round.
export const INVITATION_CREATE_ROLE_KEYS = [
    "admin",
    "company_manager",
    "facility_supervisor",
    // Postventa: invitar al propietario ES el acto de activar la unidad,
    // y lo hace el jefe de proyecto desde la ficha. Sin esto, activar una
    // unidad requeriría un admin de organización por cada vivienda.
    "gerente_postventa",
    "jefe_proyecto",
];
/** True when a role may create invitations via POST /auth/invitations. */
export function isInvitationCreateRole(role) {
    return INVITATION_CREATE_ROLE_KEYS.includes(role);
}
// ADR-0144 §2 — may load units from the inmobiliaria's spreadsheet
// (POST /assets/import/*). Org-scoped only: the sheet names projects by
// name across the whole org, so an asset_group-scoped `jefe_proyecto`
// would be writing outside its scope. `admin` is BitácoraPro staff. The
// route also accepts a platform-admin token, and the recipe gate
// (`recipe.unitProfile`) decides whether the surface exists at all.
export const UNIT_IMPORT_ROLE_KEYS = [
    "admin",
    "gerente_postventa",
];
/** True when a role may run the unit importer (ADR-0144 §2). */
export function isUnitImportRole(role) {
    return UNIT_IMPORT_ROLE_KEYS.includes(role);
}
// Which roles a given inviter role may grant via POST /auth/invitations
// (ADR-0112) — a deliberately conservative cap for this pass, not a full
// recipe-derived one (see ADR-0091's deferred recipe-aware validation).
// `company_manager` may NOT invite another `company_manager` — avoids
// self-propagating a known fail-open (a `company_manager` role a recipe
// doesn't declare resolves to org-wide `{all: true}` access in
// `apps/core-api/src/lib/scoped-assets.ts`; letting it mint more of
// itself would make that self-sustaining). `facility_supervisor` may NOT
// invite `inspector` — avoids an org-wide escalation in the
// `broker_postventa` recipe, where `inspector` is organization-scoped but
// `facility_supervisor` is only asset_group-scoped there. Neither may
// invite `admin`. `facility_supervisor` may invite `store_manager`
// because `store_manager` is scoped at or below `facility_supervisor`'s
// own level in every recipe that declares both. Revisit once a
// recipe-aware validation lands on this path.
export const INVITATION_GRANTABLE_ROLE_KEYS_BY_INVITER = {
    // Copied, not aliased — this row intentionally auto-widens to any new
    // role appended to ROLE_KEYS (unlike every other row below, which
    // requires an explicit edit to admit a new role). Spreading avoids
    // this authorization table holding a live reference to the shared
    // exported array.
    //
    // `company_manager` is deliberately excluded even here: an org-scoped
    // `admin` membership must not be able to mint a `company_manager`.
    // Only the platform-admin bypass in requireInviterAuth() (which skips
    // this map entirely) may do that — i.e. only a BitácoraPro platform
    // admin, not any org's own "admin" user. Product decision, 2026-09-03:
    // Gerente is sensitive enough (org-wide write/analytics capability,
    // and the residential_rental fail-open this file documents elsewhere)
    // that its grant stays a platform-admin-only action, full stop.
    // `gerente_postventa` se excluye por la MISMA razón que
    // `company_manager`: es su gemelo de postventa (alcance organización,
    // escritura y analytics org-wide). Si el auto-ensanchamiento de esta
    // fila lo admitiera, un admin de org podría mintear por la puerta de
    // postventa exactamente la capacidad que la puerta de facility le
    // niega. Que la fila se ensanche sola es cómodo para roles acotados y
    // peligroso para los sensibles: cada rol nuevo de alcance
    // organización tiene que sumarse a este filtro.
    admin: [...ROLE_KEYS].filter((r) => r !== "company_manager" && r !== "gerente_postventa"),
    company_manager: ["facility_supervisor", "store_manager", "brand_manager"],
    facility_supervisor: ["facility_supervisor", "store_manager"],
    brand_manager: [],
    store_manager: [],
    inspector: [],
    // Postventa. `gerente_postventa` es el análogo de company_manager:
    // puede invitar hacia abajo, nunca a otro de su nivel ni a `admin`.
    // `jefe_proyecto` invita al propietario de una unidad de sus
    // proyectos — es el acto de activación de la unidad — y a otro jefe
    // de proyecto, que está a su mismo nivel de alcance (asset_group),
    // igual que `facility_supervisor` puede invitar a otro supervisor.
    gerente_postventa: ["jefe_proyecto", "propietario"],
    jefe_proyecto: ["jefe_proyecto", "propietario"],
    // Un tercero externo no invita a nadie, nunca.
    propietario: [],
};
/** True when `inviterRole` may grant `targetRole` via POST /auth/invitations (ADR-0112). */
export function canGrantInvitationRole(inviterRole, targetRole) {
    return INVITATION_GRANTABLE_ROLE_KEYS_BY_INVITER[inviterRole].includes(targetRole);
}
// The roles whose invitation carries a per-asset assignment (ADR-0116,
// promoted from a file-local copy in
// apps/web-dashboard/src/features/admin/AdminOrgDetail.tsx). Both
// core-api invite-time stamp routes — POST /admin/orgs/:id/invitations
// (admin.ts) and POST /organizations/me/invitations (organizations.ts) —
// key their STAMP_BY_ROLE map off this exact set
// (apps/core-api/src/lib/invitation-asset-stamp.ts); the dashboard's
// store-picker UI keys its "show the picker" decision off the same set.
// A single source of truth here is the point — before this promotion the
// dashboard had its own copy that could silently drift from core-api's.
//
// `propietario` entra acá en F5 y NO es una excepción a ADR-0090: este
// es un conjunto de ESTAMPADO, no de capacidad. Dice "la invitación de
// este rol viene con una unidad pegada", que para el propietario es
// literalmente el acto de activación — sin el estampado,
// `owner_user_id` nunca se escribe y la rama de scoping por propietario
// devuelve el conjunto vacío. Los CINCO conjuntos de capacidad de más
// arriba (DASHBOARD_SHELL_ROLE_KEYS, FINDING_MANAGEMENT_ROLE_KEYS,
// MANAGEMENT_ANALYTICS_ROLE_KEYS, IA_CHAT_ROLE_KEYS,
// ASSET_SCOPED_DELEGATION_EDITOR_ROLE_KEYS) siguen sin él a propósito, y
// el test por complemento de auth.test.ts los vigila — este conjunto de
// estampado queda fuera de esa lista porque estampar una unidad no otorga
// ninguna capacidad sobre ella.
export const ASSET_SCOPED_INVITE_ROLE_KEYS = [
    "store_manager",
    "facility_supervisor",
    "propietario",
];
/** True when a role's invitation carries a per-store assignment (ADR-0116). */
export function isAssetScopedInviteRole(role) {
    return ASSET_SCOPED_INVITE_ROLE_KEYS.includes(role);
}
// Roles whose grant additionally requires the target org to be
// multi-brand (ADR-0115). `brand_manager` only makes sense where there is
// more than one brand to manage; granting it into a single-brand org
// would mint a role with nothing distinct to scope to. Checked by
// POST /auth/invitations AFTER canGrantInvitationRole/org-resolution, via
// a live read of core-api's brand count (GET
// /internal/orgs/:organizationId/brand-count) — not a static property of
// the role, so it cannot live in INVITATION_GRANTABLE_ROLE_KEYS_BY_INVITER
// above. That map is a static, compile-time-exhaustive lattice (which
// inviter may grant which role, period); this predicate gates a second,
// runtime, I/O-dependent precondition (does THIS org currently have more
// than one brand). Folding the two together would make the map's
// exhaustiveness guarantee misleading — a reader could no longer tell,
// from the map alone, whether a `true` entry is unconditionally grantable
// or conditional on live org state.
export const MULTIBRAND_REQUIRED_ROLE_KEYS = ["brand_manager"];
/** True when granting `targetRole` requires the target org to be multi-brand (ADR-0115). */
export function requiresMultibrandOrg(targetRole) {
    return MULTIBRAND_REQUIRED_ROLE_KEYS.includes(targetRole);
}
// Roles whose scope is ONE brand and whose access token carries the JWT
// claim `brand` (ADR-0164). auth-svc emits the claim only for these roles;
// core-api fills `AuthContext.scopeBrandId` only for these roles. The
// fitness test requires every recipe memberRole with scopeType "brand" to
// be in this set, in READ_ONLY_ROLE_KEYS and in
// MULTIBRAND_REQUIRED_ROLE_KEYS.
export const BRAND_SCOPED_ROLE_KEYS = ["brand_manager"];
/**
 * True when a role is scoped to one brand and carries the JWT claim
 * `brand` (ADR-0164). Plain `boolean`, not a type predicate — see the
 * note on `isRecipeMemberRole` for why the predicate form mis-narrows.
 */
export function isBrandScopedRole(role) {
    return BRAND_SCOPED_ROLE_KEYS.includes(role);
}
// Read-only roles in v1 (founder decision 2026-10-19, ADR-0164). core-api
// rejects their mutating requests in onRequest with 403 ROLE_READ_ONLY,
// media-svc rejects them on /media/presign-upload, and the dashboard hides
// every write affordance for them. Kept separate from
// BRAND_SCOPED_ROLE_KEYS even though both hold the same role today:
// "scoped to a brand" and "may not write" are two decisions, and a future
// brand role that writes must not be read-only by accident.
export const READ_ONLY_ROLE_KEYS = ["brand_manager"];
/**
 * True when a role may not mutate anything (read-only in v1, ADR-0164).
 * Plain `boolean`, not a type predicate — see `isRecipeMemberRole`.
 */
export function isReadOnlyRole(role) {
    return READ_ONLY_ROLE_KEYS.includes(role);
}
/**
 * True when `role` is one of the target org's recipe-declared member roles.
 *
 * Deliberately returns plain `boolean`, not a `role is Role` type
 * predicate. A `Role` the recipe doesn't declare is still a `Role` — the
 * predicate form would have TypeScript narrow the negative branch to
 * `never` at call sites where the input's static type is already `Role`
 * (e.g. `body.role` in organizations.ts), which is an incorrect narrowing,
 * not a real invariant. That bogus `never` then fails
 * `@typescript-eslint/restrict-template-expressions` wherever the negative
 * branch interpolates the value into a template literal.
 */
export function isRecipeMemberRole(role, recipe) {
    return recipe.memberRoles.some((r) => r.key === role);
}
/**
 * Runs the three-part invitation-grant gate against a single target role:
 * escalation cap, recipe membership, then the multibrand gate. Returns
 * the first denial reason, or `null` when the grant is allowed.
 *
 * `opts.platformAdmin: true` skips ONLY the escalation-cap check — a
 * platform admin still can't grant a role the target org's recipe
 * doesn't declare. `opts.brandCount: null` (unknown — loading/error) is
 * treated as "not multibrand," failing closed on that gate, matching
 * this file's other fail-closed conventions.
 */
export function invitationGrantDenial(inviterRole, targetRole, recipe, opts) {
    if (!opts.platformAdmin &&
        (inviterRole === null || !canGrantInvitationRole(inviterRole, targetRole))) {
        return "ROLE_ESCALATION";
    }
    if (!isRecipeMemberRole(targetRole, recipe))
        return "ROLE_NOT_IN_RECIPE";
    if (requiresMultibrandOrg(targetRole) &&
        !(recipe.brands?.enabled === true && (opts.brandCount ?? 0) > 1)) {
        return "SINGLE_BRAND_ORG";
    }
    return null;
}
/**
 * The full set of roles `inviterRole` may grant right now, against this
 * one target org's recipe and brand count — the recipe-derived
 * replacement for the retired `FACILITY_ONBOARDING_ROLE_KEYS` static
 * list. Used by platform-admin-facing invite pickers (dashboard
 * /admin/orgs/:id, the workbook ingest UI) that need "every role this
 * inviter could pick," not a single grant/deny answer.
 */
export function grantableInvitationRoles(inviterRole, recipe, opts) {
    return recipe.memberRoles
        .map((r) => r.key)
        .filter((k) => ROLE_KEYS.includes(k))
        .filter((r) => invitationGrantDenial(inviterRole, r, recipe, opts) === null);
}
/**
 * True when the target org's recipe declares `role` with scopeType
 * "brand". An entry without `scopeType` (an older core-api's policy) falls
 * back to `isBrandScopedRole`; a role the recipe does not declare is
 * false.
 */
export function isBrandScopedInviteRole(role, policy) {
    const entry = policy.memberRoles.find((r) => r.key === role);
    if (entry === undefined)
        return false;
    if (entry.scopeType === undefined)
        return isBrandScopedRole(role);
    return entry.scopeType === "brand";
}
/**
 * The brand-scope gate of an invitation. Returns the first denial, or
 * `null` when allowed:
 *   1. a role that is not brand-scoped with `scopeBrandId` → BRAND_NOT_ALLOWED
 *   2. a brand-scoped role without `scopeBrandId`         → BRAND_REQUIRED
 *   3. `scopeBrandId` not among the org's brands          → BRAND_NOT_IN_ORG
 *
 * `orgBrandIds: null` means the org's brand list is unknown (an older
 * core-api that does not send `brandIds`); it fails closed on step 3.
 */
export function invitationBrandScopeDenial(role, scopeBrandId, policy, orgBrandIds) {
    const brandScoped = isBrandScopedInviteRole(role, policy);
    if (!brandScoped && scopeBrandId !== undefined)
        return "BRAND_NOT_ALLOWED";
    if (brandScoped && scopeBrandId === undefined)
        return "BRAND_REQUIRED";
    if (scopeBrandId !== undefined && (orgBrandIds === null || !orgBrandIds.has(scopeBrandId))) {
        return "BRAND_NOT_IN_ORG";
    }
    return null;
}
// --- Brand of affiliation (ADR-0165) ------------------------------------
//
// The body field core-api's invitation routes take for a role whose recipe
// declares `brandIdentity: "affiliation"` (the facility supervisor): the
// one brand it belongs to. Derived off the brand's own id schema, not
// hand-written (`affiliationBrandId` in the self-service and admin
// invitation bodies).
export const invitationAffiliationSchema = brandSummarySchema.pick({ id: true });
/**
 * The one-brand gate (ADR-0165 §5) over the stores a role would end up
 * with. Returns the first denial, or `null` when allowed:
 *   1. the stores' brands are two or more              → BRAND_SPANS_MULTIPLE
 *   2. a store has no brand (multi-brand org only)      → STORE_BRAND_UNRESOLVED
 *   3. the one brand is not `affiliationBrandId`        → AFFILIATION_MISMATCH
 *
 * `effectiveBrandIds` holds one entry per store: its own brand, else its
 * zone's, never the org's default brand (`null` when neither has one).
 * `affiliationBrandId: null` means the brand is not known yet (a pending
 * link): then only rules 1-2 apply — the zone must be single-brand. In a
 * single-brand org nothing can span or mismatch, so it always allows.
 */
export function brandCoherenceDenial(effectiveBrandIds, affiliationBrandId, orgIsMultiBrand) {
    if (!orgIsMultiBrand)
        return null;
    const brands = new Set(effectiveBrandIds.filter((b) => b !== null));
    if (brands.size > 1)
        return "BRAND_SPANS_MULTIPLE";
    if (effectiveBrandIds.includes(null))
        return "STORE_BRAND_UNRESOLVED";
    if (affiliationBrandId !== null && brands.size === 1 && !brands.has(affiliationBrandId)) {
        return "AFFILIATION_MISMATCH";
    }
    return null;
}
/**
 * True when an invitation for `role` must be bound to an email: the
 * asset-scoped roles (the per-store stamp matches the accepting user by
 * email, ADR-0116) and the brand-scoped roles (a brand manager is always
 * invited to a specific person, founder 2026-10-19). Lives here and not
 * in invitations.ts, which already imports from this file.
 */
export function isEmailRequiredInviteRole(role) {
    return isAssetScopedInviteRole(role) || isBrandScopedRole(role);
}
// May read the org member roster surface (dashboard /equipo, PWA
// /cuenta/equipo): every colleague's email, displayName, last-seen
// heartbeat and scope assignment. Product decision, 2026-09-03 — amends
// ADR-0089's Sidebar-level safe default at both ends.
//
// `facility_supervisor` is IN. ADR-0089 shipped the Equipo nav item
// hidden from it as a conservative default "pending a product decision";
// that decision arrived and reversed it — supervisors are the ones who
// onboard jefes de tienda, and they are already inviter-eligible, so the
// roster is their working surface, not a courtesy read.
//
// `store_manager` ("Jefe de tienda") is OUT. A jefe manages one store's
// operation, not the org's people; they are not inviter-eligible either,
// so there is no action behind the read — only every colleague's email
// and activity heartbeat.
//
// `brand_manager` stays IN, unchanged this round.
//
// Scope note: GET /auth/memberships carries no role gate server-side
// today. This set gates the *rendered roster surface*, not the endpoint.
export const TEAM_ROSTER_READ_ROLE_KEYS = [
    "admin",
    "company_manager",
    "brand_manager",
    "facility_supervisor",
];
/** True when a role may read the org member roster surface (/equipo). */
export function isTeamRosterReadRole(role) {
    return TEAM_ROSTER_READ_ROLE_KEYS.includes(role);
}
// Exported (Arrow pilot, 2026-08-10) so the inline provider-create body
// in findings.ts reuses this exact shape rather than a second one —
// security-engineer's explicit recommendation, since the field feeds a
// mail `to:` and case-variant duplicates are a catalog-pollution vector
// identical to providers.shortName's.
export const emailSchema = z
    .string()
    .min(3)
    .max(254)
    .email()
    // Stored lower-cased; the input is normalized at the boundary.
    .transform((v) => v.toLowerCase());
const passwordSchema = z
    .string()
    .min(8, { message: "Password must be at least 8 characters" })
    .max(256);
export const registerRequestSchema = z.object({
    email: emailSchema,
    password: passwordSchema,
    displayName: z.string().min(1).max(120),
    // Two ways to satisfy registration:
    //   - `organizationName` → bootstrap a new organization (open path,
    //     gated off when AUTH_INVITE_ONLY=true)
    //   - `invitationToken`  → join an existing organization with the role
    //     the invitation specified
    // Exactly one of the two must be provided; the route validates that.
    organizationName: z.string().min(1).max(120).optional(),
    recipeSlug: z.string().min(1).max(64).optional(),
    invitationToken: z.string().min(1).optional(),
    // Cloudflare Turnstile token. Required when TURNSTILE_SECRET_KEY is
    // set on the server; ignored otherwise. The widget on the frontend
    // produces this string.
    turnstileToken: z.string().min(1).optional(),
});
export const loginRequestSchema = z.object({
    email: emailSchema,
    password: z.string().min(1).max(256),
    // Optional 6-digit TOTP code. Only required when the email matches
    // PLATFORM_ADMIN_EMAILS and the user has already enrolled MFA
    // (ADR-0024). If missing on an enrolled admin, server returns
    // { requiresTotpCode: true } and the client re-submits with code.
    totpCode: z
        .string()
        .regex(/^\d{6}$/)
        .optional(),
});
// Login response is a discriminated union (ADR-0024):
//   - kind: "token-pair"        — normal flow, includes JWT pair.
//   - kind: "totp-required"     — admin enrolled, code missing/invalid.
//   - kind: "totp-enrollment"   — admin first login, must enrol.
//
// The token-pair shape is reused below as the canonical TokenPairResponse
// since /auth/verify-email and /auth/refresh also issue it.
export const totpRequiredResponseSchema = z.object({
    kind: z.literal("totp-required"),
});
export const totpEnrollmentResponseSchema = z.object({
    kind: z.literal("totp-enrollment"),
    // Inline SVG QR for authenticator-app scanning.
    qrSvg: z.string(),
    // otpauth:// URI for manual entry — never log this.
    provisioningUri: z.string(),
});
// Body for POST /auth/totp/enroll. Re-authenticates with email +
// password to bind the enrollment to a known identity, then verifies
// the 6-digit code against the secret stored at first-login.
export const totpEnrollRequestSchema = z.object({
    email: emailSchema,
    password: z.string().min(1).max(256),
    totpCode: z.string().regex(/^\d{6}$/),
});
export const refreshRequestSchema = z.object({
    refreshToken: z.string().min(1),
});
export const logoutRequestSchema = z.object({
    refreshToken: z.string().min(1),
});
// PATCH /auth/me — update profile fields on the authenticated user.
// Both fields are optional — passing only one updates that field. Email
// and password mutations are deliberately routed through their own
// verification-bearing flows, not this endpoint. See ADR-0018.
export const updateMeRequestSchema = z
    .object({
    rut: z.string().nullable().optional(),
    displayName: z.string().trim().min(1).max(120).optional(),
    // El carrusel de onboarding se muestra una vez por USUARIO, no por
    // dispositivo: guardarlo en `localStorage` lo hacía reaparecer en el
    // teléfono nuevo del propietario. Va versionado porque el onboarding
    // cambia: subir la versión vuelve a mostrarlo una vez a todos, sin
    // borrar nada. El cliente manda la versión que acaba de mostrar.
    onboardingSeenVersion: z.string().trim().min(1).max(32).optional(),
})
    .strict()
    .refine((v) => v.rut !== undefined || v.displayName !== undefined || v.onboardingSeenVersion !== undefined, { message: "At least one field is required" });
export const updateMeResponseSchema = z.object({
    user: z.object({
        id: z.string().uuid(),
        email: z.string(),
        displayName: z.string(),
        rut: z.string().nullable(),
    }),
});
// GET /auth/me — read the authenticated user's profile + their org. Used
// by the FE's /cuenta screen so it can render without re-logging-in, and
// so it can refresh user/org data after a PATCH /auth/me round-trip.
//
// No tokens here — this is purely a read of who-am-I. The bearer token
// the caller already holds is the proof of identity; we just return the
// data that goes with it.
export const meResponseSchema = z.object({
    user: z.object({
        id: z.string().uuid(),
        email: z.string(),
        displayName: z.string(),
        rut: z.string().nullable(),
        emailVerified: z.boolean(),
        // True when the user's email is in auth-svc's PLATFORM_ADMIN_EMAILS
        // allowlist. Carried so the FE can render the internal /admin panel
        // (org provisioning, vertical testing). Treat as advisory — backend
        // re-checks the same claim on every privileged endpoint.
        platformAdmin: z.boolean(),
        /** La versión del onboarding que este usuario ya vio, o `null` si
         *  nunca vio ninguno. Es la fuente durable del flag: `localStorage`
         *  quedó como espejo local, no como verdad. */
        onboardingSeenVersion: z.string().nullable(),
    }),
    organization: z.object({
        id: z.string().uuid(),
        name: z.string(),
        role: roleSchema,
    }),
});
export const tokenPairResponseSchema = z.object({
    accessToken: z.string(),
    accessTokenExpiresAt: z.string().datetime(),
    refreshToken: z.string(),
    refreshTokenExpiresAt: z.string().datetime(),
    user: z.object({
        id: z.string().uuid(),
        email: z.string(),
        displayName: z.string(),
        // Personal RUT for verticals that source the inspector's idDoc
        // from the authenticated user (residential_rental → corredor.source
        // = "session_user"). Nullable — pre-profile users haven't set it.
        rut: z.string().nullable(),
        platformAdmin: z.boolean(),
    }),
    organization: z.object({
        id: z.string().uuid(),
        name: z.string(),
        role: roleSchema,
    }),
});
// --- Cross-app session handoff (ADR-0045) -----------------------------
// PWA → gerencial dashboard, "land already logged in". The PWA mints a
// one-time, short-TTL opaque code at POST /auth/handoff (Bearer-auth'd),
// then redirects to the dashboard which exchanges it at
// POST /auth/handoff/exchange (code-bearing, otherwise unauthenticated)
// for a fresh TokenPairResponse it stores in its own localStorage.
//
// The code is opaque and carries no identity — the session is re-derived
// live from auth-svc's own membership tables at exchange time, so a code
// can never out-live a role change. A refresh token never travels in a
// URL; only the one-time code does, and it is stripped from history on
// arrival. Mint fails closed (403) for roles outside DASHBOARD_SHELL_ROLE_KEYS.
export const handoffMintResponseSchema = z.object({
    code: z.string(),
    expiresAt: z.string().datetime(),
});
export const handoffExchangeRequestSchema = z.object({
    code: z.string().min(1),
});
// The exchange response is the canonical tokenPairResponseSchema above —
// reused verbatim so the dashboard's session store ingests it with the
// same parser it already uses for /login and /refresh.
// Register has two response shapes, discriminated on `verificationRequired`:
//
//   - Open registration (organizationName supplied): the user creates their
//     own org and we hard-gate on email verification (ADR-0005). Response
//     omits tokens; caller redirects to "check your email"; tokens are
//     minted by /auth/verify-email once the link is consumed.
//   - Invitation registration (invitationToken supplied): the invitation
//     token already proved email ownership (only the invited recipient
//     could have it). We mark the user verified inline, mint a token pair
//     directly, and return them so the caller can log the user in
//     immediately. No second email.
const registerUserShape = z.object({
    id: z.string().uuid(),
    email: z.string(),
    displayName: z.string(),
    rut: z.string().nullable(),
    platformAdmin: z.boolean(),
});
const registerOrgShape = z.object({
    id: z.string().uuid(),
    name: z.string(),
    role: roleSchema,
});
export const registerVerificationRequiredResponseSchema = z.object({
    user: registerUserShape,
    organization: registerOrgShape,
    verificationRequired: z.literal(true),
});
export const registerWithTokensResponseSchema = z.object({
    user: registerUserShape,
    organization: registerOrgShape,
    verificationRequired: z.literal(false),
    accessToken: z.string(),
    accessTokenExpiresAt: z.string().datetime(),
    refreshToken: z.string(),
    refreshTokenExpiresAt: z.string().datetime(),
});
export const registerResponseSchema = z.discriminatedUnion("verificationRequired", [
    registerVerificationRequiredResponseSchema,
    registerWithTokensResponseSchema,
]);
export const verifyEmailRequestSchema = z.object({
    token: z.string().min(1),
});
// Convenience: caller can also re-trigger the email if the first message
// got lost. Always returns 204 to avoid leaking which addresses are
// registered.
export const requestEmailVerificationSchema = z.object({
    email: emailSchema,
});
export const requestPasswordResetSchema = z.object({
    email: emailSchema,
    turnstileToken: z.string().min(1).optional(),
});
// --- Invitations -----------------------------------------------------------
export const createInvitationRequestSchema = z.object({
    // Optional: when set, only that exact address may redeem the token.
    // When omitted the invitation is "open" — anyone with the link joins
    // the org as `role`. Both modes are useful (specific hire vs.
    // share-in-Slack) so the wire shape supports both.
    email: emailSchema.optional(),
    role: roleSchema,
    // Optional: target organization. Org-admins are forced into their own
    // org (the field is ignored / must equal their JWT's `org`). Platform
    // admins (PLATFORM_ADMIN_EMAILS allowlist) can pass any org id to
    // invite people into an org they don't belong to — that's the only
    // way to seed the first admin of a freshly-provisioned org from the
    // internal /admin panel.
    organizationId: z.string().uuid().optional(),
    // Optional: the invitee's full name, sourced from the xlsx `usuarios`
    // sheet (`nombreCompleto`, max 255). The core-api ingest already sends
    // this on the dispatch payload; auth-svc snapshots it on the invitation
    // so the accept form can pre-fill/hide the name field and the created
    // user's displayName is org-asserted, not redeemer-typed. Absent for
    // open / asset-derived invitations that carry no name.
    displayName: z.string().trim().min(1).max(255).optional(),
    // Who the invitation reads as coming FROM in the email — for xlsx
    // ingest, the invitee's store manager (jefe de tienda) for supervisors,
    // the org's gerente for jefes — not the platform-admin who ran the
    // ingest. Used at send time only (not persisted). Absent on manual
    // invites, where the email falls back to the actual inviting user.
    inviterName: z.string().trim().min(1).max(255).optional(),
    // ADR-0164: required when the role declares scopeType "brand" in the
    // target org's recipe; forbidden for any other role. auth-svc validates
    // it (invitationBrandScopeDenial). The schema stays NOT strict on
    // purpose: the workbook commit and retry-failed still send
    // scopeAssetGroupId and scopeAssetId.
    scopeBrandId: z.string().uuid().optional(),
});
export const createInvitationResponseSchema = z.object({
    invitationId: z.string().uuid(),
    token: z.string(),
    // Pre-built URL the admin can copy/paste — convenience.
    acceptUrl: z.string().url(),
    expiresAt: z.string().datetime(),
    // Sin `accountExists` / `accountInSameOrg` (revisión de la fase 1, p2):
    // se calculaban sobre las membresías de TODAS las organizaciones, así
    // que un invitador de la org A podía saber si un correo cualquiera
    // tenía cuenta en otra org cliente. Ese cálculo sigue en el servidor
    // (decide el copy del correo y si se envía), pero no sale de auth-svc.
    // Las superficies de activación muestran siempre el enlace con un copy
    // neutro.
    // Echo of the persisted brand scope (ADR-0164). Optional so an older
    // auth-svc response still parses.
    scopeBrandId: z.string().uuid().nullable().optional(),
});
// POST /auth/invitations/:token/accept — redeem an email-bound invitation
// with an EXISTING, signed-in account (ADR-0143). The register path keeps
// handling redemption-with-a-new-account; this one associates the
// invitation's org/role with the session's user instead of creating a
// user. `membership` says what the server did with the membership row:
//   "existing"    — the user already had an ACTIVE `propietario`
//                   membership in the org; nothing was created, the
//                   invitation was just consumed (and the asset backfill
//                   fired). A staff membership never lands here — the
//                   route answers 409 INVITATION_EMAIL_BELONGS_TO_STAFF
//                   instead, and the token survives for a re-invite.
//   "created"     — first membership of the account (edge: an account
//                   with zero memberships).
//   "reactivated" — a soft-disabled `propietario` membership in the same
//                   org was re-enabled with the invitation's role (only
//                   when no ACTIVE membership exists in another org;
//                   otherwise 409 MEMBERSHIP_IN_OTHER_ORGANIZATION).
export const acceptInvitationResponseSchema = z.object({
    organizationId: z.string().uuid(),
    organizationName: z.string(),
    role: roleSchema,
    membership: z.enum(["existing", "created", "reactivated"]),
});
// Public preview a prospective invitee sees before filling out the
// registration form. Carries the invitee's bound email (requiredEmail)
// and name (inviteeName) ONLY for email-bound invitations, where the
// token-holder is constrained to that same person at redemption. These
// are personal data, disclosed on a pre-contractual basis (Ley 21.719
// art. 12). Open/shareable invitations (email IS NULL) omit both — the
// token is forwardable, so no identifiable PII is attached to it.
// Public, minimized projection of the inviting org's default brand,
// served unconditionally on the unauthenticated preview. Derived from
// the canonical `brandSummarySchema` (ADR-0033 §C1) rather than a flat
// bespoke field — only `name`/`logoUrl` are surfaced; `id` and
// `logoUpdatedAt` are dropped for the public threat model. The logo is
// org branding (a legal entity's mark), not invitee PII, so it is not
// email-bound like requiredEmail/inviteeName. Null when the org has no
// default brand or no logo set. See ADR-0042 for the auth-svc→brands
// cross-service read this field is populated by.
export const invitationBrandSchema = brandSummarySchema.pick({
    name: true,
    logoUrl: true,
});
export const invitationPreviewResponseSchema = z.object({
    organizationId: z.string().uuid(),
    organizationName: z.string(),
    // Inviting org's default-brand logo, for the accept-invitation hero.
    // Sibling of organizationName (both are legal-entity branding,
    // disclosed unconditionally), never inside the email-bound spread.
    brand: invitationBrandSchema.nullable(),
    role: roleSchema,
    // When the invitation is bound to a specific email, surface it so the
    // frontend can show "Sign up as <email>" instead of letting the user
    // pick anything. Omitted for open invitations.
    requiredEmail: z.string().optional(),
    // The invitee's name, snapshotted from the xlsx at invite time. Returned
    // only for email-bound invitations (see comment above); lets the accept
    // form hide the name field. Omitted for open invitations and when no
    // name was carried.
    inviteeName: z.string().optional(),
    // ADR-0143 — true when the bound email already has an account, so the
    // accept page offers "inicia sesión para aceptar" instead of the
    // registration form. Only ever present on email-bound invitations: the
    // bound address is already disclosed on this preview, and the bit is
    // only readable by whoever holds the single-use token. Omitted for open
    // invitations (no email to look up, and the token is forwardable).
    accountExists: z.boolean().optional(),
    expiresAt: z.string().datetime(),
});
export const resetPasswordRequestSchema = z.object({
    token: z.string().min(1),
    newPassword: passwordSchema,
});
// --- Org self-management (caller's own organization) ------------------
// Used by /cuenta/settings (#0112). The full Organization row lives in
// core-api's schema; auth-svc never updates it. These shapes are
// shared because the wire contract belongs at the package boundary,
// even though the routes live in core-api.
export const myOrganizationResponseSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    // Org-level RUT (b2b verticals source corredor identity from
    // `session_organization`). Nullable for orgs that haven't set one —
    // most arriendos orgs leave it null and source RUT per-user.
    rut: z.string().nullable(),
    recipeSlug: z.string(),
    // Count of `brands` rows the org owns (ADR-0115). Optional — not the
    // multimarca *authorization* gate (that is server-enforced at
    // POST /auth/invitations against core-api's internal brand-count
    // endpoint, requiresMultibrandOrg in this file) — this is a separate,
    // lower-stakes consumer: the dashboard's own invite-picker filtering
    // hides `brand_manager` as an offered option when the caller's org has
    // 1 or fewer brands. Optional so a client hiding the option gracefully
    // degrades to always-offer if it ignores the field; the real gate
    // still rejects server-side either way.
    brandCount: z.number().int().nonnegative().optional(),
    // Logo of the org's default brand (`brands.config.logoUrl`, ADR-0033
    // §C2) — the dashboard sidebar shows it in place of the org initials.
    // Optional + nullable so an older core-api or a brand without a logo
    // falls back to the initials square. Null for brand-scoped roles
    // (ADR-0164): a brand manager never sees another brand's logo.
    defaultBrandLogoUrl: z.string().nullable().optional(),
    // Corporate logo of the organization itself (`organizations.logo_url`,
    // ADR-0164), separate from every brand logo. Optional + nullable.
    logoUrl: z.string().url().nullable().optional(),
    // The caller's own brand, for the sidebar logo (ADR-0164):
    //   - ABSENT when the caller's scope is the whole org.
    //   - brand-scoped role: its brand (JWT claim `brand`, filtered by org),
    //     or null when there is no claim or the brand no longer exists.
    //   - any other role (jefe de tienda, supervisor, ...): the one
    //     effective brand ALL of its stores share, resolved WITHOUT the
    //     default-brand fallback (assets.brand_id, else
    //     asset_groups.brand_id — founder 2026-10-19). null when any store
    //     has no brand, the stores span several brands, or there are none.
    callerBrand: brandSummarySchema
        .pick({ id: true, name: true, logoUrl: true })
        .nullable()
        .optional(),
    // Every brand of the org — present ONLY when the caller's scope is the
    // whole org (the Equipo brand picker).
    brands: z.array(brandSummarySchema.pick({ id: true, name: true })).optional(),
    // Sidebar logo chain, the same for every role with no role branching:
    //   [callerBrand?.logoUrl, logoUrl, defaultBrandLogoUrl]
    //     → drop empties → dedupe → first one, else the initials square.
});
// Admin-only update. Both fields are optional — passing only one
// updates that field. recipeSlug is intentionally not editable here:
// switching verticals is a platform-admin operation in /admin and
// would invalidate cached recipes across the FE.
export const myOrganizationUpdateBodySchema = z
    .object({
    name: z.string().trim().min(1).max(120).optional(),
    rut: z.string().trim().nullable().optional(),
})
    .strict()
    .refine((v) => v.name !== undefined || v.rut !== undefined, {
    message: "At least one field is required",
});
// --- Org members + pending invitations --------------------------------
// Used by /cuenta/equipo (#0110) to render the team list. Any
// authenticated user can read; mutations stay admin-only.
export const orgMemberSchema = z.object({
    userId: z.string().uuid(),
    email: z.string(),
    displayName: z.string(),
    role: roleSchema,
    joinedAt: z.string().datetime(),
    // Hour-level activity heartbeat, written on /auth/refresh. NULL
    // means the user has not refreshed a token since the column landed
    // (or never signed in at all). The dashboard renders "—" in that case.
    lastSeenAt: z.string().datetime().nullable(),
    // Soft-deactivation timestamp. NULL = active membership. Set via
    // PATCH /auth/memberships/:id. A non-null value means: the
    // membership is invisible to JWT-emission paths (login/refresh/me),
    // existing access tokens expire on their normal cycle (~15 min),
    // and the dashboard renders the row greyed. Optional on the wire
    // during the rolling deploy so old FE bundles ignore unknown keys.
    disabledAt: z.string().datetime().nullable().optional(),
    // Scope-aware membership (ADR-0020 / ADR-0022). At most ONE is
    // non-null per row; all three null means "organization scope" (the
    // pre-ADR-0020 behavior preserved for arriendos / postventa).
    // Soft refs to brands / asset_groups / assets owned by core-api;
    // the dashboard resolves names via a forthcoming POST /assets/resolve
    // on core-api (see ADR-0022). Optional on the wire so older FE
    // bundles ignore the unknown keys.
    scopeBrandId: z.string().uuid().nullable().optional(),
    scopeAssetGroupId: z.string().uuid().nullable().optional(),
    scopeAssetId: z.string().uuid().nullable().optional(),
    // True for the org's owner — the oldest ACTIVE membership row, same
    // "oldest createdAt, disabledAt IS NULL" definition the
    // PATCH /auth/memberships/:id owner-deactivation guard already uses
    // internally (ADR-0119). Computed from the unfiltered roster before
    // any `?view=roster` filtering (memberships.ts), so ownership is a
    // fact independent of whether that row is later hidden by the filter.
    // The PWA reads this instead of a `members[0]` heuristic that
    // misattributes "Propietario" once filtering ships.
    isOwner: z.boolean(),
});
export const orgMemberListResponseSchema = z.object({
    members: z.array(orgMemberSchema),
});
// --- PATCH /auth/memberships/:id (admin-only, tenant-scoped) ----------
// Used to soft-deactivate (disabledAt = ISO timestamp) or reactivate
// (disabledAt = null) a membership. The handler enforces: caller is
// admin or platform admin, the target's organization_id matches the
// caller's org claim, the caller is not deactivating themselves, and
// the target is not the org's owner (oldest active membership).
export const patchMembershipRequestSchema = z.object({
    // Pass an ISO datetime to disable, null to reactivate. `undefined`
    // is not accepted — the FE must commit to one direction or the other.
    disabledAt: z.string().datetime().nullable(),
});
export const patchMembershipResponseSchema = z.object({
    membership: orgMemberSchema,
});
// Pending invitation as seen by /cuenta/equipo. Distinct from the
// CreateInvitationResponse: this is a list-view of existing invitations,
// not the token-handoff shape returned at creation time. We never echo
// the raw token here — only the metadata the FE needs to render the
// pending row.
export const orgInvitationListItemSchema = z.object({
    id: z.string().uuid(),
    // Null when the invitation is open (share-by-link). The FE renders
    // those as "Cualquier persona con el link" rather than an address.
    email: z.string().nullable(),
    role: roleSchema,
    invitedByUserId: z.string().uuid(),
    expiresAt: z.string().datetime(),
    createdAt: z.string().datetime(),
});
export const orgInvitationListResponseSchema = z.object({
    invitations: z.array(orgInvitationListItemSchema),
});
// --- GET /admin/orgs/:id/members --------------------------------------
// Platform-admin-only endpoint per ADR-0033. Lets the apps/web-dashboard
// admin panel read the roster of any org without the operator having a
// membership in it. Gated by auth-svc's own `requirePlatformAdmin` (the
// caller's JWT carries `platform_admin: true`).
//
// Returns active AND disabled rows. The `status` field is derived from
// `disabledAt`: "active" when null, "disabled" when set. Sort: email
// ASC, membership id ASC as deterministic tiebreaker.
//
// PII access is logged in core-api's audit_events as
// `platform_admin.org_pii_read` per Ley 21.719 art. 11 (the dashboard
// fans out both queries; core-api writes the audit row on the
// /admin/orgs/:id call that the dashboard makes alongside this one).
export const adminOrgMemberSchema = z.object({
    userId: z.string().uuid(),
    email: z.string(),
    displayName: z.string(),
    role: roleSchema,
    status: z.enum(["active", "disabled"]),
    joinedAt: z.string().datetime(),
});
export const adminOrgMembersResponseSchema = z.object({
    members: z.array(adminOrgMemberSchema),
});
//# sourceMappingURL=auth.js.map